import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import Divider from '@material-ui/core/Divider'
import TextField from '@material-ui/core/TextField'
import Switch from '@material-ui/core/Switch'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import React, { useEffect, useMemo, useState } from 'react'
import { Button } from '../../../../components/common/Buttons'
import Typography from '../../../../components/common/Typography'
import { useAlertContext } from '../../../../contexts/AlertContext'
import connect from '../../connect'

const DEFAULT_ANNOUNCEMENT_BANNER = {
  active: false,
  startDate: '',
  endDate: '',
  title: '',
  message: '',
  color: 'green',
}

const COLORS = [
  {
    label: 'Green',
    value: 'green',
    color: '#17b471',
  },
  {
    label: 'Yellow',
    value: 'yellow',
    color: '#f6c343',
  },
  {
    label: 'Red',
    value: 'red',
    color: '#d93025',
  },
]

const DISABLED_BUTTON_COLOR = '#c4c4c4'
const SAVE_BUTTON_COLOR = '#17b471'

const formatDateValue = value => {
  if (!value) {
    return ''
  }

  return String(value).slice(0, 10)
}

const normalizeAnnouncementBanner = value => ({
  active: Boolean(value.active),
  startDate: value.startDate || '',
  endDate: value.endDate || '',
  title: value.title || '',
  message: value.message || '',
  color: value.color || 'green',
})

const AnnouncementBannerConfiguration = ({
  announcementBanner,
  saveAnnouncementBanner,
}) => {
  const { successAlert, errorAlert } = useAlertContext()
  const [isSend, setIsSend] = useState(false)
  const [form, setForm] = useState(DEFAULT_ANNOUNCEMENT_BANNER)
  const [initialForm, setInitialForm] = useState(DEFAULT_ANNOUNCEMENT_BANNER)

  useEffect(() => {
    const nextForm = announcementBanner
      ? {
          active: Boolean(announcementBanner.active),
          startDate: formatDateValue(announcementBanner.startDate),
          endDate: formatDateValue(announcementBanner.endDate),
          title: announcementBanner.title || '',
          message: announcementBanner.message || '',
          color: announcementBanner.color || 'green',
        }
      : DEFAULT_ANNOUNCEMENT_BANNER

    setForm(nextForm)
    setInitialForm(nextForm)
  }, [announcementBanner])

  const hasChanges = useMemo(() => {
    return (
      JSON.stringify(normalizeAnnouncementBanner(form)) !==
      JSON.stringify(normalizeAnnouncementBanner(initialForm))
    )
  }, [form, initialForm])

  const saveDisabled = isSend || !hasChanges

  const onChange = field => ev => {
    setForm({
      ...form,
      [field]: ev.target.value,
    })
  }

  const onChangeActive = ev => {
    setForm({
      ...form,
      active: ev.target.checked,
    })
  }

  const saveSettings = async () => {
    try {
      setIsSend(true)
      await saveAnnouncementBanner(form)
      setInitialForm(form)
      setIsSend(false)
      successAlert('Announcement banner saved successfully')
    } catch (error) {
      setIsSend(false)
      errorAlert('Announcement banner failed to save')
    }
  }

  return (
    <Paper
      elevation={1}
      style={{
        padding: '24px',
        marginBottom: '24px',
        borderRadius: '8px',
      }}
    >
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item>
          <Typography weight="600" variant="h4" fontFamily="lexend">
            Announcement Banner
          </Typography>
        </Grid>
        <Grid item>
          <Grid container alignItems="center" spacing={1}>
            <Grid item>
              <Typography weight="500" variant="body1">
                Active
              </Typography>
            </Grid>
            <Grid item>
              <Switch
                checked={form.active}
                onChange={onChangeActive}
                color="primary"
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Divider style={{ margin: '24px 0' }} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Typography
            weight="600"
            variant="body1"
            style={{ marginBottom: '8px' }}
          >
            Start Date
          </Typography>
          <TextField
            fullWidth
            type="date"
            variant="outlined"
            value={form.startDate}
            onChange={onChange('startDate')}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography
            weight="600"
            variant="body1"
            style={{ marginBottom: '8px' }}
          >
            End Date
          </Typography>
          <TextField
            fullWidth
            type="date"
            variant="outlined"
            value={form.endDate}
            onChange={onChange('endDate')}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography
            weight="600"
            variant="body1"
            style={{ marginBottom: '8px' }}
          >
            Banner Title
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Enter banner title..."
            value={form.title}
            onChange={onChange('title')}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography
            weight="600"
            variant="body1"
            style={{ marginBottom: '8px' }}
          >
            Banner Content
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            variant="outlined"
            placeholder="Enter banner content..."
            value={form.message}
            onChange={onChange('message')}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography
            weight="600"
            variant="body1"
            style={{ marginBottom: '8px' }}
          >
            Banner Color
          </Typography>
          <RadioGroup row value={form.color} onChange={onChange('color')}>
            {COLORS.map(color => (
              <FormControlLabel
                key={color.value}
                value={color.value}
                control={
                  <Radio
                    style={{ color: color.color }}
                  />
                }
                label={color.label}
              />
            ))}
          </RadioGroup>
        </Grid>
      </Grid>

      <Divider style={{ margin: '24px 0' }} />

      <Button
        variant="contained"
        style={{
          background: saveDisabled ? DISABLED_BUTTON_COLOR : SAVE_BUTTON_COLOR,
          color: '#fff',
          cursor: saveDisabled ? 'not-allowed' : 'pointer',
        }}
        disabled={saveDisabled}
        pending={isSend}
        onClick={saveSettings}
      >
        Save Settings
      </Button>
    </Paper>
  )
}

export default connect(AnnouncementBannerConfiguration)